import LogArchivePanelV119 from './LogArchivePanelV119';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  useVirtualizer,
  elementScroll,
  observeElementOffset,
  observeElementRect,
  windowScroll,
  observeWindowOffset,
  observeWindowRect,
} from '@tanstack/react-virtual';
import { getLogPageOffsetV115, getLogPageScrollerV115 } from './logScrollV115';
import {
  Accordion,
  Button,
  Checkbox,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  Pagination,
  Segment,
  Select,
  Table,
} from 'semantic-ui-react';
import {
  API,
  copy,
  isAdmin,
  isRoot,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';
import { useTranslation } from 'react-i18next';
import { logCursorParamsV1016, acceptLogCursorV1016 } from './logCursorV1016';

import { renderColorLabel, renderQuota } from '../helpers/render';
import { Link } from 'react-router-dom';
import RecordExportV1023 from './RecordExportV1023';
import { recordExportTextV1023, resetLogFiltersV1023 } from './recordExportHelpersV1023';
import './LogsTable_v7.css';
import './logQueryLayoutV101.css';
import {
  useSessionViewFieldV81,
  useSessionViewStateV81,
} from '../hooks/useSessionViewStateV81';
import BillingDateCalendarV89, {
  serializeDateIntervalsV89,
} from './BillingDateCalendarV89';
import useBillingDateSelectionV97, {
  resolveDateSelectionV97,
} from '../pages/Billing/BillingDateSelectionStateV97';
import ResizableHeaderCellV815 from './ResizableHeaderCellV815';
import {
  commercialSourceV107,
  commercialMultiplierV107,
  commercialReceiptStateV107,
} from './commercialLogV107';
import useResizableColumnsV815 from '../hooks/useResizableColumnsV815';
import CustomerLogRequestDetailV822 from './CustomerLogRequestDetailV822';
import { RetrySummaryV1021, retryTextV1022 } from './retryAuditV1021';
import {
  compareCustomerMoneyV1014,
  customerMoneyTextV1014,
  customerMoneyFieldsV1014,
  customerMoneyV1014,
} from './customerMoneyV1014';
import RequestAuditV97 from './requestAuditV97';
import { logPrincipalV823 } from './logIdentityV823';
import { logProviderNameV106 } from './logProviderV106';
import LogClientIdentityV94 from './LogClientIdentityV94';
import { logClientIdentityV94 } from './clientDisplayV94';
import { MODEL_BRANDS_V10 } from '../pages/Model/modelBrandsV10';
import { MODEL_CATEGORY_FILTERS_V814 } from '../pages/Model/modelPresentationV814';
import {
  customerLogAmountV822,
  customerLogEndpointsV822,
  customerLogParamsV822,
  customerLogSafeRequestV822,
  customerLogStorageV822,
  customerLogTextV822,
  customerLogUsageV822,
  customerLogValueV822,
  sanitizeCustomerLogV822,
} from './customerLogV822';

const ROOT_PAGE_SIZE_OPTIONS_V105 = [
  { key: 100, text: '100', value: 100 },
  { key: 250, text: '250', value: 250 },
  { key: 500, text: '500', value: 500 },
  { key: 1000, text: '1000', value: 1000 },
  { key: 5000, text: '5000', value: 5000 },
];

const USER_PAGE_SIZE_OPTIONS_V105 = [10, 25, 50, 100].map((value) => ({
  key: value,
  text: String(value),
  value,
}));

const LOG_COLUMN_STORAGE_KEY_V7 = 'alfredapi.log.visible_columns.v8.15';
const LEGACY_LOG_COLUMN_STORAGE_KEY_V7 = 'alfredapi.log.visible_columns.v7.2';
const DEFAULT_VISIBLE_COLUMNS_V7 = [
  'request_received_at',
  'time',
  'channel',
  'client_app',
  'channel_key',
  'type',
  'model',
  'username',
  'organization',
  'token_name',
  'request_group',
  'routing_group',
  'performance',
  'prompt_tokens',
  'completion_tokens',
  'source_name',
  'effective_multiplier',
  'quota',
  'detail',
];
const CUSTOMER_LOG_COLUMNS_V822 = [
  'provider_family',
  'customer_scope',
  'resolution',
  'actual_model',
];
const VALID_LOG_COLUMNS_V7 = new Set([
  ...DEFAULT_VISIBLE_COLUMNS_V7,
  ...CUSTOMER_LOG_COLUMNS_V822,
]);
const LOG_COLUMN_WIDTHS_V7 = Object.freeze({
  request_received_at: 168,
  time: 168,
  channel: 160,
  client_app: 180,
  channel_key: 96,
  type: 84,
  model: 180,
  username: 120,
  organization: 160,
  token_name: 150,
  request_group: 112,
  routing_group: 112,
  performance: 154,
  prompt_tokens: 92,
  completion_tokens: 92,
  quota: 112,
  detail: 360,
  provider_family: 160,
  customer_scope: 180,
  resolution: 112,
  actual_model: 180,
  source_name: 160,
  effective_multiplier: 112,
});

function loadVisibleColumnsV7(storageKey, customerScope) {
  try {
    const currentValue = window.localStorage.getItem(storageKey);
    const isLegacyValue = currentValue === null;
    const parsed = JSON.parse(
      currentValue ??
        (customerScope === 'ordinary'
          ? window.localStorage.getItem(LOG_COLUMN_STORAGE_KEY_V7) ??
            window.localStorage.getItem(LEGACY_LOG_COLUMN_STORAGE_KEY_V7)
          : null)
    );
    if (Array.isArray(parsed)) {
      const validColumns = parsed.filter((column) =>
        VALID_LOG_COLUMNS_V7.has(column)
      );
      if (isLegacyValue && !validColumns.includes('request_received_at')) {
        validColumns.unshift('request_received_at');
      }
      if (isLegacyValue && !validColumns.includes('client_app')) {
        validColumns.push('client_app');
      }
      if (isLegacyValue && !validColumns.includes('organization')) {
        validColumns.push('organization');
      }
      const commercialKey = `${storageKey}.commercial_v107`;
      if (window.localStorage.getItem(commercialKey) !== '1') {
        for (const column of ['source_name', 'effective_multiplier']) {
          if (!validColumns.includes(column)) validColumns.push(column);
        }
        window.localStorage.setItem(commercialKey, '1');
      }
      if (validColumns.length > 0) return validColumns;
    }
  } catch {}
  return [...DEFAULT_VISIBLE_COLUMNS_V7];
}

function renderTimestamp(timestamp, request_id) {
  return (
    <code
      onClick={async () => {
        if (await copy(request_id)) {
          showSuccess(`已复制请求 ID：${request_id}`);
        } else {
          showWarning(`请求 ID 复制失败：${request_id}`);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {timestamp2string(timestamp)}
    </code>
  );
}

function renderRequestReceivedTimestamp(timestamp) {
  if (!timestamp) return <span>-</span>;
  return <code>{timestamp2string(timestamp)}</code>;
}

const getDisplayModelNameV4 = (log) =>
  (log?.requested_model_name || log?.model_name || '').trim();

function renderClientIdentityV73(log) {
  return <LogClientIdentityV94 log={log} />;
}

const isErrorLogV72 = (log) => {
  const content = String(log?.content || '').toLowerCase();
  const outcome = String(log?.outcome || '').toLowerCase();
  const errorCode = String(log?.error_code || '').toLowerCase();
  const httpStatus = Number(log?.http_status) || 0;
  if (outcome === 'canceled' || errorCode === 'client_request_canceled') {
    return false;
  }
  return (
    outcome === 'failure' ||
    outcome === 'failed' ||
    httpStatus >= 400 ||
    log?.type === 4 ||
    content.includes('错误') ||
    content.includes('error') ||
    content.includes('request rejected')
  );
};

function renderType(type) {
  switch (type) {
    case 1:
      return (
        <Label basic color='green'>
          充值
        </Label>
      );
    case 2:
      return (
        <Label basic color='olive'>
          消费
        </Label>
      );
    case 3:
      return (
        <Label basic color='orange'>
          管理
        </Label>
      );
    case 4:
      return (
        <Label basic color='purple'>
          系统
        </Label>
      );
    case 5:
      return (
        <Label basic color='violet'>
          测试
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          未知
        </Label>
      );
  }
}

function getColorByElapsedTime(elapsedTime) {
  if (!Number.isFinite(elapsedTime) || elapsedTime <= 0) return 'black';
  if (elapsedTime < 1000) return 'green';
  if (elapsedTime < 3000) return 'olive';
  if (elapsedTime < 5000) return 'yellow';
  if (elapsedTime < 10000) return 'orange';
  return 'red';
}

function formatMillisecondsV7(value) {
  const milliseconds = Number(value) || 0;
  if (milliseconds <= 0) return '-';
  if (milliseconds < 1000) return `${milliseconds} ms`;
  return `${(milliseconds / 1000).toFixed(2)} s`;
}

function renderPerformanceV7(log, t) {
  const elapsedTime = Number(log.elapsed_time) || 0;
  const firstResponseTime = Number(log.first_response_time) || 0;
  const speed =
    elapsedTime > 0 && log.completion_tokens
      ? log.completion_tokens / (elapsedTime / 1000)
      : 0;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '4px',
        minWidth: '112px',
      }}
    >
      <Label basic size='mini' color={getColorByElapsedTime(elapsedTime)}>
        {t('log.performance.total')}: {formatMillisecondsV7(elapsedTime)}
      </Label>
      <Label basic size='mini' color={getColorByElapsedTime(firstResponseTime)}>
        {t('log.performance.first_response')}:{' '}
        {formatMillisecondsV7(firstResponseTime)}
      </Label>
      {speed > 0 && (
        <Label basic size='mini' color='blue'>
          {t('log.performance.speed')}: {speed.toFixed(2)} t/s
        </Label>
      )}
    </div>
  );
}

function renderDetail(log, openDetail, t) {
  const detailText = typeof log.content === 'string' ? log.content : '';
  const detailSummary = detailText.replace(/\s+/g, ' ').trim();
  return (
    <div className='log-detail-cell-v7'>
      {detailSummary || log.request_id ? (
        <>
          <div className='log-detail-summary-v7' title={detailSummary}>
            {detailSummary || '-'}
          </div>
          <Button
            basic
            compact
            size='mini'
            className='log-detail-action-v7'
            onClick={() => openDetail(log)}
          >
            <Icon name='expand arrows alternate' />
            {t('log.modal.title')}
          </Button>
        </>
      ) : (
        <span className='log-detail-empty-v7'>-</span>
      )}
      {(log.is_stream || log.system_prompt_reset) && (
        <div className='log-detail-tags-v7'>
          {log.is_stream && (
            <Label size='mini' color='pink'>
              Stream
            </Label>
          )}
          {log.system_prompt_reset && (
            <Label basic size='mini' color='red'>
              System Prompt Reset
            </Label>
          )}
        </div>
      )}
    </div>
  );
}

export const renderTextLogString = (log, admin = true) => {
  let typeStr =
    log.type === 1
      ? '充值'
      : log.type === 2
      ? '消费'
      : log.type === 3
      ? '管理'
      : log.type === 4
      ? '系统'
      : '其他';
  let timeStr = timestamp2string(log.created_at);
  let isWorker =
    log.content && log.content.includes('Edge Proxy')
      ? 'Worker: YES'
      : 'Worker: NO';
  let isStreamStr = log.is_stream ? 'Stream: YES' : 'Stream: NO';
  let latencyStr = log.elapsed_time
    ? (log.elapsed_time / 1000).toFixed(2) + 's'
    : '-';
  let firstResponseStr = log.first_response_time
    ? (log.first_response_time / 1000).toFixed(2) + 's'
    : '-';
  let speed =
    log.elapsed_time && log.completion_tokens
      ? (log.completion_tokens / (log.elapsed_time / 1000)).toFixed(2)
      : '-';

  let str = `[${timeStr}] [${typeStr}] [${isWorker}] [${isStreamStr}]\n`;
  str += admin
    ? `[路由] ReqID: ${log.request_id || '-'} | Channel: ${
        log.channel_name || (log.channel ? `#${log.channel}` : '-')
      } (KeyIdx: ${customerLogValueV822(log.channel_key_index)})\n`
    : `[Request ID] ${log.request_id || '-'}\n`;
  str +=
    '[Client] App: ' +
    (logClientIdentityV94(log).name || '-') +
    ' | Version: ' +
    (logClientIdentityV94(log).version || '-') +
    ' | SDK: ' +
    (log.client_sdk || '-') +
    ' | SDK Version: ' +
    (log.client_sdk_version || '-') +
    '\n';
  str +=
    '[Client Runtime] ' +
    (log.client_runtime || '-') +
    ' | Origin: ' +
    (log.client_origin || '-') +
    ' | Detection: ' +
    (log.client_detection_source || '-') +
    ' / ' +
    (log.client_detection_confidence || '-') +
    '\n';
  str += '[User-Agent] ' + (log.client_user_agent || '-') + '\n';
  if (admin)
    str += `[协议] Client: ${log.client_request_format || '-'} | Provider: ${
      log.provider_request_format || '-'
    } | Mode: ${log.protocol_mode || '-'}\n`;
  str += `[结果] Outcome: ${log.outcome || '-'} | HTTP: ${
    log.http_status || '-'
  } | ErrorCode: ${log.error_code || '-'}\n`;
  str += retryTextV1022(
    log.retry_audit_v1021 || log.retry_summary_v1021,
    log.request_id
  );
  if (admin)
    str += `[分组] Billing: ${log.request_group || '-'} | Routing: ${
      log.routing_group || '-'
    }\n`;
  if (admin)
    str += `[会话] Organization: ${
      log.organization_name ||
      (log.billing_account_id ? `#${log.billing_account_id}` : '-')
    } | User: ${logPrincipalV823(
      log.username,
      log.user_id
    )} | AuthToken: ${logPrincipalV823(log.token_name, log.token_id)}\n`;
  str += `[模型] ${getDisplayModelNameV4(log) || '-'}\n`;
  if (admin)
    str += `[Provider] Channel: ${logProviderNameV106(log)} | Project: ${
      log.project_reference || '-'
    } | Service Tier: ${log.service_tier || '-'}\n`;
  const prompt = customerLogUsageV822(log, 'prompt_tokens');
  const completion = customerLogUsageV822(log, 'completion_tokens');
  const total =
    prompt === '-' || completion === '-'
      ? '-'
      : Number(prompt) + Number(completion);
  str += `[用量] Prompt: ${prompt} | Completion: ${completion} | Total: ${total}\n`;
  str += `[Money] ${customerMoneyTextV1014(log)}\n`;
  str += `[性能] 总耗时: ${latencyStr} | 首字/首事件: ${firstResponseStr} | 平均出字速率: ${speed} t/s\n`;
  if (log.system_prompt_reset) {
    str += `[附加] System Prompt Reset: YES\n`;
  }
  str += `[详情] ${log.content || 'N/A'}\n`;
  return str;
};

const LogsTableViewV822 = ({ customerScope, isAdminUser, isRootUser, resetVersion = 0 }) => {
  const tableScrollRefV113 = useRef(null);
  const tableBodyRefV115 = useRef(null);
  const [tableBodyOffsetV115, setTableBodyOffsetV115] = useState(0);
  const [pageScrollerV115, setPageScrollerV115] = useState(() => window);
  const cursorV1016 = useRef({ cursors: {} });
  const listAbortV1016 = useRef(null);
  const statAbortV114 = useRef(null);
  const beginListRequestV1016 = () => {
    listAbortV1016.current?.abort();
    statAbortV114.current?.abort();
    statRequestRefV97.current++;
    const controller = new AbortController();
    listAbortV1016.current = controller;
    return controller;
  };
  useEffect(() => () => {
    listAbortV1016.current?.abort();
    statAbortV114.current?.abort();
  }, []);
  const { t, i18n } = useTranslation();
  const pageSizeOptionsV105 = isRootUser
    ? ROOT_PAGE_SIZE_OPTIONS_V105
    : USER_PAGE_SIZE_OPTIONS_V105;
  const [logDebugEnabled, setLogDebugEnabled] = useState(false);
  const showDiagnostics = isRootUser || logDebugEnabled;
  const [organizationLogs, setOrganizationLogs] = useState(false);
  const showUsername = showDiagnostics || organizationLogs;
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const applyRetryAuditV1022 = useCallback((requestID, audit) => {
    setSelectedLog((current) =>
      current?.request_id === requestID
        ? { ...current, retry_audit_v1021: audit }
        : current
    );
  }, []);
  const clearPrivateLogsV105 = useCallback(() => {
    if (isRootUser) return;
    setLogDebugEnabled(false);
    setLogs([]);
    setSelectedLog(null);
  }, [isRootUser]);
  const applyDetailDisclosureV105 = useCallback(
    (enabled, request) => {
      if (isRootUser) return;
      setLogDebugEnabled(enabled);
      if (!enabled) {
        setLogs((current) =>
          current.map((log) => ({
            ...customerLogSafeRequestV822(log),
            content: '',
            log_debug_enabled: false,
          }))
        );
        setSelectedLog((current) =>
          current?.request_id === request?.request_id ? request : null
        );
      }
    },
    [isRootUser]
  );
  const [loading, setLoading] = useState(true);
  const [resetQueryV1023, setResetQueryV1023] = useState(0);
  const lastResetV1023 = useRef(0);
  const storage = customerLogStorageV822(customerScope, isAdminUser);
  const endpoints = customerLogEndpointsV822(customerScope, isAdminUser);
  const customerText = (key) => customerLogTextV822(t, key);
  const formatLogTextV822 = (log) => {
    const identity =
      organizationLogs && !showDiagnostics
        ? `[User] ${logPrincipalV823(log.username, log.user_id)}\n`
        : '';
    return (
      identity +
      renderTextLogString(log, isRootUser || log.log_debug_enabled === true)
    );
  };
  const normalizeLogsV822 = (data) =>
    (Array.isArray(data) ? data : data?.items || []).map((log) =>
      isRootUser || log.log_debug_enabled === true
        ? sanitizeCustomerLogV822(log)
        : {
            ...customerLogSafeRequestV822(log),
            content: log.log_debug_enabled === false ? log.content : '',
            log_debug_enabled: false,
          }
    );
  const [billingDayV89, setBillingDayV89] = useState(null);
  const [includeArchivesV119, setIncludeArchivesV119] = useState(false);
  const [storedViewState, setViewState] = useSessionViewStateV81(
    storage.session,
    {
      showStat: false,
      activePage: 1,
      pageSize: 100,
      logType: 0,
      viewMode: 'table',
      errorOnly: false,
      userInputErrorOnly: false,
      columnSelectorOpen: false,
      inputs: {
        username: '',
        billing_account_id: '',
        token_name: '',
        model_name: '',
        provider_family: '',
        request_id: '',
        outcome: '',
        routing_group: '',
        model_company: '',
        model_type: '',
      },
    }
  );
  const validPageSizeV105 = pageSizeOptionsV105.some(
    (option) => option.value === storedViewState.pageSize
  );
  // Normalize before the first query, including a former Root session's window.
  const viewState = validPageSizeV105
    ? storedViewState
    : { ...storedViewState, pageSize: 100, activePage: 1 };
  const restoredDiagnosticFilterV105 = useRef(
    Boolean(viewState.inputs?.routing_group)
  );
  const diagnosticFiltersReadyV105 =
    !isRootUser && restoredDiagnosticFilterV105.current && logDebugEnabled;
  useEffect(() => {
    if (!validPageSizeV105) setViewState({ pageSize: 100, activePage: 1 });
  }, [validPageSizeV105, setViewState]);
  const {
    dates: selectedDatesV89,
    setDates: setSelectedDatesV89,
    today,
    refreshToday,
    preset: datePresetV97,
  } = useBillingDateSelectionV97(storage.session, billingDayV89);
  const [appliedSelectionV97, setAppliedSelectionV97] = useState({
    preset: 'today',
    dates: [],
  });
  const appliedDateIntervalsV89 = serializeDateIntervalsV89(
    resolveDateSelectionV97(appliedSelectionV97, billingDayV89, today)
  );
  const dateIntervalsV89 = serializeDateIntervalsV89(selectedDatesV89);
  const [showStat, setShowStat] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'showStat'
  );
  const [activePage, setActivePage] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'activePage'
  );
  const [pageSize, setPageSize] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'pageSize'
  );
  const previousPageV115 = useRef([activePage, pageSize]);
  useEffect(() => {
    const changed = previousPageV115.current[0] !== activePage ||
      previousPageV115.current[1] !== pageSize;
    previousPageV115.current = [activePage, pageSize];
    const table = tableScrollRefV113.current;
    if (!table) return;
    table.scrollTop = 0;
    if (changed && table.getBoundingClientRect().top < 80) {
      const scroller = getLogPageScrollerV115(table);
      scroller.scrollTo({
        top: Math.max(0, getLogPageOffsetV115(table, scroller) - 80),
        behavior: 'instant',
      });
    }
  }, [activePage, pageSize]);
  const [logType, setLogType] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'logType'
  );
  const [viewMode, setViewMode] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'viewMode'
  );
  const [errorOnly, setErrorOnly] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'errorOnly'
  );
  const [userInputErrorOnly, setUserInputErrorOnly] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'userInputErrorOnly'
  );
  const [columnSelectorOpen, setColumnSelectorOpen] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'columnSelectorOpen'
  );
  const [inputs, setInputs] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'inputs'
  );
  const [visibleColumns, setVisibleColumns] = useState(() =>
    loadVisibleColumnsV7(storage.columns, customerScope)
  );
  const [organizationsV114, setOrganizationsV114] = useState([]);
  const [organizationQueryV114, setOrganizationQueryV114] = useState(null);
  const organizationOptionsV815 = useMemo(() => [
    { key: 'all', value: '', text: t('log.organization_all_v815') },
    ...organizationsV114.map((organization) => ({
      key: String(organization.id), value: String(organization.id),
      text: organization.name || `#${organization.id}`,
    })),
  ], [organizationsV114, t]);
  const {
    widths: columnWidthsV815,
    startResize: startColumnResizeV815,
    resetColumn: resetColumnWidthV815,
    resetAll: resetColumnWidthsV815,
  } = useResizableColumnsV815(LOG_COLUMN_WIDTHS_V7, storage.widths);
  const initialPageRef = useRef(Math.max(1, Number(activePage) || 1));
  const windowFirstPageV114 = useRef(initialPageRef.current);
  const listRequestRefV97 = useRef(0);
  const statRequestRefV97 = useRef(0);
  const enterQueryTimerV101 = useRef(null);
  useEffect(
    () => () => {
      listRequestRefV97.current += 1;
      statRequestRefV97.current += 1;
      window.clearTimeout(enterQueryTimerV101.current);
    },
    []
  );
  const queryConfigRef = useRef({
    appliedDateIntervalsV89,
    includeArchivesV119,
    errorOnly,
    userInputErrorOnly,
    logType,
    pageSize,
  });
  const {
    username,
    billing_account_id,
    token_name,
    model_name,
    provider_family = '',
    request_id = '',
    outcome = '',
    routing_group = '',
    model_company = '',
    model_type = '',
  } = inputs;

  const [stat, setStat] = useState({
    quota: null,
    token: null,
  });

  useEffect(() => {
    if (lastResetV1023.current === resetVersion) return;
    lastResetV1023.current = resetVersion;
    listAbortV1016.current?.abort();
    statAbortV114.current?.abort();
    setOrganizationQueryV114(null);
    setOrganizationsV114([]);
    listRequestRefV97.current++;
    statRequestRefV97.current++;
    window.clearTimeout(enterQueryTimerV101.current);
    enterQueryTimerV101.current = null;
    cursorV1016.current = { cursors: {} };
    initialPageRef.current = 1;
    windowFirstPageV114.current = 1;
    restoredDiagnosticFilterV105.current = false;
    setViewState(resetLogFiltersV1023());
    setIncludeArchivesV119(false);
    setSelectedDatesV89([], { preset: 'today' });
    setAppliedSelectionV97({ preset: 'today', dates: [] });
    setSelectedLog(null);
    setLogs([]);
    setStat({ quota: null, token: null });
    setResetQueryV1023(resetVersion);
  }, [resetVersion, setViewState, setSelectedDatesV89]);

  const buildParamsV822 = useCallback(
    (selectedIntervals = appliedDateIntervalsV89, extra = {}) =>
      customerLogParamsV822(
        {
          type: logType,
          username,
          billing_account_id,
          token_name,
          model_name,
          date_intervals: selectedIntervals,
          archive_mode: includeArchivesV119 ? 'include' : 'recent',
          provider_family: isRootUser ? '' : provider_family,
          channel_query: isRootUser ? provider_family : '',
          request_id,
          outcome,
          routing_group: showDiagnostics ? routing_group : '',
          model_company,
          model_type,
          error_only: errorOnly,
          user_input_error_only: userInputErrorOnly,
          ...extra,
        },
        customerScope,
        isAdminUser
      ),
    [
      appliedDateIntervalsV89,
      includeArchivesV119,
      logType,
      username,
      billing_account_id,
      token_name,
      model_name,
      provider_family,
      request_id,
      outcome,
      routing_group,
      showDiagnostics,
      model_company,
      model_type,
      errorOnly,
      userInputErrorOnly,
      customerScope,
      isAdminUser,
      isRootUser,
    ]
  );

  useEffect(() => {
    if (!isAdminUser || organizationQueryV114 === null) return;
    const abort = new AbortController();
    API.get('/api/log/organizations', {
      params: JSON.parse(organizationQueryV114), signal: abort.signal,
    })
      .then((response) => {
        if (abort.signal.aborted || !response?.data?.success) return;
        setOrganizationsV114(response.data.data || []);
      })
      .catch(() => {});
    return () => abort.abort();
  }, [isAdminUser, organizationQueryV114]);

  useEffect(() => {
    if (isAdminUser) return;
    let active = true;
    API.get('/api/billing/v8/self/summary')
      .then((response) => {
        const day = Number(response?.data?.data?.account?.billing_day);
        if (active && day >= 1 && day <= 31) setBillingDayV89(day);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [isAdminUser]);

  const LOG_OPTIONS = [
    { key: '0', text: t('log.type.all'), value: 0 },
    { key: '1', text: t('log.type.topup'), value: 1 },
    { key: '2', text: t('log.type.usage'), value: 2 },
    { key: '3', text: t('log.type.admin'), value: 3 },
    { key: '4', text: t('log.type.system'), value: 4 },
    { key: '5', text: t('log.type.test'), value: 5 },
  ];
  const filterOptionsV101 = (values) =>
    values.map((value) => ({
      key: value || 'all',
      value,
      text: t(`log.filters_v101.${value || 'all'}`),
    }));
  const companyOptionsV101 = [
    { key: 'all', value: '', text: t('log.filters_v101.all') },
    ...Object.entries(MODEL_BRANDS_V10).map(([value, company]) => ({
      key: value,
      value,
      text:
        value === 'custom' ? t('log.filters_v101.other_company') : company.name,
    })),
  ];

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const getLogStat = async (selectedIntervals = appliedDateIntervalsV89) => {
    const request = ++statRequestRefV97.current;
    statAbortV114.current?.abort();
    const abort = new AbortController();
    statAbortV114.current = abort;
    let res;
    try {
      res = await API.get(endpoints.stat, {
        params: buildParamsV822(selectedIntervals), signal: abort.signal,
      });
    } catch (error) {
      if (abort.signal.aborted) return;
      throw error;
    }
    if (abort.signal.aborted || request !== statRequestRefV97.current) return;
    if (!res?.data) {
      setStat({ quota: null });
      return;
    }
    const { success, message, data } = res.data;
    if (success) {
      setStat(data);
    } else {
      showError(message);
    }
  };
  const getLogSelfStat = getLogStat;

  const handleEyeClick = async () => {
    try {
      if (!showStat) {
        if (isAdminUser) {
          await getLogStat();
        } else {
          await getLogSelfStat();
        }
      }
      if (showStat) {
        statAbortV114.current?.abort();
        statRequestRefV97.current++;
      }
      setShowStat(!showStat);
    } catch (reason) {
      showError(reason);
    }
  };

  const showUserTokenQuota = () => {
    return logType !== 5;
  };

  const columnOptionsV7 = [
    { key: 'request_received_at', label: t('log.table.request_received_at') },
    { key: 'time', label: t('log.table.time') },
    { key: 'channel', label: t('log.table.channel'), adminOnly: true },
    { key: 'client_app', label: t('log.table.client_app'), adminOnly: true },
    {
      key: 'channel_key',
      label: t('log.table.channel_key'),
      adminOnly: true,
    },
    { key: 'type', label: t('log.table.type') },
    { key: 'model', label: t('log.table.model') },
    {
      key: 'username',
      label: t('log.table.username'),
      usernameOnly: true,
      usageOnly: true,
    },
    {
      key: 'organization',
      label: t('log.table.organization'),
      adminOnly: true,
    },
    { key: 'token_name', label: t('log.table.token_name'), usageOnly: true },
    {
      key: 'request_group',
      label: t('log.table.request_group'),
      adminOnly: true,
    },
    {
      key: 'routing_group',
      label: t('log.table.routing_group'),
      adminOnly: true,
    },
    { key: 'performance', label: t('log.table.performance') },
    {
      key: 'prompt_tokens',
      label: t('log.table.prompt_tokens'),
      usageOnly: true,
    },
    {
      key: 'completion_tokens',
      label: t('log.table.completion_tokens'),
      usageOnly: true,
    },
    { key: 'source_name', label: 'Source', usageOnly: true },
    {
      key: 'effective_multiplier',
      label: t('log.table.multiplier', { defaultValue: 'Multiplier' }),
      usageOnly: true,
    },
    {
      key: 'quota',
      label: t('log.table.actual_charge', { defaultValue: 'Actual charge' }),
      usageOnly: true,
    },
    { key: 'detail', label: t('log.table.detail') },
    {
      key: 'provider_family',
      label: customerText(isRootUser ? 'channel_provider' : 'provider'),
      rootOnly: true,
    },
    {
      key: 'customer_scope',
      label: customerText('classification'),
      rootOnly: true,
    },
    { key: 'resolution', label: customerText('resolution'), rootOnly: true },
    {
      key: 'actual_model',
      label: customerText('actual_model'),
      rootOnly: true,
    },
  ];

  const availableColumnOptionsV7 = columnOptionsV7.filter(
    (column) =>
      (!column.adminOnly || showDiagnostics) &&
      (!column.usernameOnly || showUsername) &&
      (!column.rootOnly || showDiagnostics) &&
      (!column.usageOnly || showUserTokenQuota())
  );
  const isColumnVisibleV7 = (column) =>
    visibleColumns.includes(column) &&
    availableColumnOptionsV7.some((option) => option.key === column);
  const visibleColumnOptionsV7 = availableColumnOptionsV7.filter((column) =>
    isColumnVisibleV7(column.key)
  );
  const visibleColumnCountV7 = visibleColumnOptionsV7.length;
  const tableMinWidthV7 = visibleColumnOptionsV7.reduce(
    (total, column) => total + columnWidthsV815[column.key],
    0
  );

  const toggleColumnV7 = (column, checked) => {
    if (!checked && visibleColumnCountV7 <= 1) {
      showWarning(t('log.columns.keep_one'));
      return;
    }
    setVisibleColumns((current) => {
      if (checked) {
        return current.includes(column) ? current : [...current, column];
      }
      return current.filter((item) => item !== column);
    });
  };

  const resetColumnsV7 = () => {
    setVisibleColumns([...DEFAULT_VISIBLE_COLUMNS_V7]);
    resetColumnWidthsV815();
  };

  const loadLogs = async (
    startIdx,
    selectedIntervals = appliedDateIntervalsV89
  ) => {
    const request = ++listRequestRefV97.current;
    const abort = beginListRequestV1016();
    const params = logCursorParamsV1016(
      cursorV1016.current,
      buildParamsV822(selectedIntervals, {
        p: startIdx,
        page_size: pageSize,
      }),
      customerScope
    );
    let res;
    try {
      res = await API.get(endpoints.list, { params, signal: abort.signal });
    } catch (error) {
      if (abort.signal.aborted) return;
      if (request === listRequestRefV97.current) clearPrivateLogsV105();
      throw error;
    }
    if (request !== listRequestRefV97.current) return;
    if (!res?.data) {
      clearPrivateLogsV105();
      setLoading(false);
      return;
    }
    const { success, message, data } = res.data;
    if (success) {
      acceptLogCursorV1016(cursorV1016.current, res.data, startIdx);
      if (isAdminUser)
        setOrganizationQueryV114(JSON.stringify(buildParamsV822(selectedIntervals)));
      setOrganizationLogs(res.data.organization_logs === true);
      const debugEnabled = res.data.log_debug_enabled === true;
      setLogDebugEnabled(debugEnabled);
      if (!debugEnabled && !isRootUser) setSelectedLog(null);
      if (startIdx === 0) {
        windowFirstPageV114.current = 1;
        setLogs(normalizeLogsV822(data));
      } else {
        let newLogs =
          debugEnabled || isRootUser
            ? [...logs]
            : logs.map((log) => ({
                ...customerLogSafeRequestV822(log),
                content: '',
                log_debug_enabled: false,
              }));
        const nextLogs = normalizeLogsV822(data);
        newLogs.splice((startIdx + 1 - windowFirstPageV114.current) * pageSize, nextLogs.length, ...nextLogs);
        setLogs(newLogs);
      }
    } else {
      clearPrivateLogsV105();
      showError(message);
    }
    setLoading(false);
    return success === true;
  };

  const loadLogWindowV81 = async (
    pageCount,
    selectedIntervals = appliedDateIntervalsV89
  ) => {
    const request = ++listRequestRefV97.current;
    const abort = beginListRequestV1016();
    const initialParams = buildParamsV822(selectedIntervals, {
      p: Math.max(0, pageCount - 1),
      page_size: pageSize,
    });
    const params =
      pageCount === 1
        ? logCursorParamsV1016(
            cursorV1016.current,
            initialParams,
            customerScope
          )
        : initialParams;
    if (pageCount !== 1) cursorV1016.current = { cursors: {} };
    let res;
    try {
      res = await API.get(endpoints.list, { params, signal: abort.signal });
    } catch (error) {
      if (abort.signal.aborted) return;
      if (request === listRequestRefV97.current) clearPrivateLogsV105();
      throw error;
    }
    if (request !== listRequestRefV97.current) return;
    if (!res?.data) {
      clearPrivateLogsV105();
      setLoading(false);
      return;
    }
    const { success, message, data } = res.data;
    if (success) {
      if (pageCount === 1)
        acceptLogCursorV1016(cursorV1016.current, res.data, 0);
      if (isAdminUser)
        setOrganizationQueryV114(JSON.stringify(buildParamsV822(selectedIntervals)));
      setOrganizationLogs(res.data.organization_logs === true);
      setLogDebugEnabled(res.data.log_debug_enabled === true);
      if (res.data.log_debug_enabled !== true && !isRootUser)
        setSelectedLog(null);
      windowFirstPageV114.current = pageCount;
      setLogs(normalizeLogsV822(data));
    } else {
      clearPrivateLogsV105();
      showError(message);
    }
    setLoading(false);
    return success === true;
  };

  const onPaginationChange = (e, { activePage }) => {
    const query = queryConfigRef.current;
    (async () => {
      if (activePage < windowFirstPageV114.current) {
        if (!(await loadLogWindowV81(activePage))) return;
      } else if (activePage === windowFirstPageV114.current + Math.ceil(logs.length / pageSize)) {
        // In this case we have to load more data and then append them.
        if (!(await loadLogs(activePage - 1))) return;
      }
      if (query !== queryConfigRef.current) return;
      setActivePage(activePage);
    })().catch((reason) => {
      showError(reason);
      setLoading(false);
    });
  };

  const refresh = async () => {
    if (!dateIntervalsV89) {
      showError(t('billing.calendar.empty_selection'));
      return;
    }
    setLoading(true);
    setActivePage(1);
    setAppliedSelectionV97({
      preset: datePresetV97,
      dates: selectedDatesV89,
    });
    if (dateIntervalsV89 !== appliedDateIntervalsV89) return;
    try {
      const request = listRequestRefV97.current + 1;
      const success = await loadLogs(0, dateIntervalsV89);
      if (success && showStat && request === listRequestRefV97.current)
        await getLogStat(dateIntervalsV89);
    } catch (reason) {
      showError(reason);
      setLoading(false);
    }
  };

  const captureQueryEnterV101 = (event) => {
    if (event.key !== 'Enter') return;
    if (
      event.nativeEvent.isComposing ||
      event.nativeEvent.keyCode === 229 ||
      event.repeat ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleQueryEnterV101 = (event) => {
    if (event.key !== 'Enter') return;
    const form = event.currentTarget.closest('form');
    event.preventDefault();
    window.clearTimeout(enterQueryTimerV101.current);
    // Dropdown selection updates first; submit the committed form, not stale state.
    enterQueryTimerV101.current = window.setTimeout(() => {
      enterQueryTimerV101.current = null;
      if (form?.isConnected) form.requestSubmit();
    }, 0);
  };

  useEffect(() => {
    if (resetQueryV1023 !== resetVersion) return;
    const previousConfig = queryConfigRef.current;
    const initialConfig =
      previousConfig.appliedDateIntervalsV89 === appliedDateIntervalsV89 &&
      previousConfig.errorOnly === errorOnly &&
      previousConfig.userInputErrorOnly === userInputErrorOnly &&
      previousConfig.logType === logType &&
      previousConfig.pageSize === pageSize &&
      previousConfig.includeArchivesV119 === includeArchivesV119;
    const pageCount = initialConfig ? initialPageRef.current : 1;
    queryConfigRef.current = {
      appliedDateIntervalsV89,
      includeArchivesV119,
      errorOnly,
      userInputErrorOnly,
      logType,
      pageSize,
    };
    if (!initialConfig) {
      initialPageRef.current = 1;
      setActivePage(1);
    }
    if (enterQueryTimerV101.current !== null) return;
    setLoading(true);
    const request = listRequestRefV97.current + 1;
    loadLogWindowV81(pageCount).then((success) => {
      if (success && showStat && request === listRequestRefV97.current)
        return getLogStat();
    }).catch((reason) => {
      showError(reason);
      setLoading(false);
    });
    // Submitted filters start with a page before optional aggregate work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedDateIntervalsV89,
    includeArchivesV119,
    logType,
    pageSize,
    errorOnly,
    userInputErrorOnly,
    diagnosticFiltersReadyV105,
    resetQueryV1023,
    resetVersion,
  ]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        storage.columns,
        JSON.stringify(visibleColumns)
      );
    } catch {}
  }, [visibleColumns, storage.columns]);

  const visibleLogsV114 = useMemo(() => logs.filter((log) => !log.deleted), [logs]);
  const currentPageLogsV114 = useMemo(() =>
    visibleLogsV114.slice(
      (activePage - windowFirstPageV114.current) * pageSize,
      (activePage + 1 - windowFirstPageV114.current) * pageSize
    ), [visibleLogsV114, activePage, pageSize]);
  const getVisibleLogsV2 = () => visibleLogsV114;
  const getCurrentPageLogsV2 = () => currentPageLogsV114;
  const virtualizedV114 = viewMode === 'table' && currentPageLogsV114.length > 100;
  const rowKeyV114 = useCallback((index) => {
    const log = currentPageLogsV114[index];
    return log?.id || `${log?.request_id || 'legacy'}:${log?.created_at}:${log?.type}:${index}`;
  }, [currentPageLogsV114]);
  // Mobile CSS scrolls body; desktop scrolls window. Neither bounds the table.
  const measurePageV115 = useCallback(() => {
    const body = tableBodyRefV115.current;
    if (!body) return;
    const scroller = getLogPageScrollerV115(tableScrollRefV113.current);
    setPageScrollerV115(scroller);
    setTableBodyOffsetV115(getLogPageOffsetV115(body, scroller));
  }, []);
  useLayoutEffect(measurePageV115);
  useEffect(() => {
    const body = tableBodyRefV115.current;
    if (!body) return undefined;
    const observer = typeof ResizeObserver === 'undefined'
      ? null : new ResizeObserver(measurePageV115);
    observer?.observe(tableScrollRefV113.current.parentElement);
    observer?.observe(body.previousElementSibling);
    window.addEventListener('resize', measurePageV115);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measurePageV115);
    };
  }, [viewMode, measurePageV115]);
  const windowScrollerV115 = pageScrollerV115 === window;
  const rowVirtualizerV114 = useVirtualizer({
    getScrollElement: () => pageScrollerV115,
    observeElementRect: windowScrollerV115 ? observeWindowRect : observeElementRect,
    observeElementOffset: windowScrollerV115 ? observeWindowOffset : observeElementOffset,
    scrollToFn: windowScrollerV115 ? windowScroll : elementScroll,
    initialOffset: () => windowScrollerV115 ? window.scrollY : pageScrollerV115.scrollTop,
    count: virtualizedV114 ? currentPageLogsV114.length : 0,
    estimateSize: () => 100,
    getItemKey: rowKeyV114,
    overscan: 6,
    initialRect: { width: 1000, height: 700 },
    scrollMargin: tableBodyOffsetV115,
    enabled: virtualizedV114,
  });
  const virtualRowsV114 = rowVirtualizerV114.getVirtualItems();
  const renderedRowsV114 = virtualizedV114
    ? virtualRowsV114
    : currentPageLogsV114.map((_, index) => ({ index, key: rowKeyV114(index) }));
  const paddingTopV114 = virtualizedV114
    ? Math.max(0, (virtualRowsV114[0]?.start || 0) - tableBodyOffsetV115)
    : 0;
  const paddingBottomV114 = virtualizedV114
    ? Math.max(0, rowVirtualizerV114.getTotalSize() -
      ((virtualRowsV114.at(-1)?.end || tableBodyOffsetV115) - tableBodyOffsetV115))
    : 0;
  useEffect(() => {
    rowVirtualizerV114.measure();
  }, [rowVirtualizerV114, currentPageLogsV114, columnWidthsV815, visibleColumns]);

  const getTotalPagesV2 = () => {
    if (cursorV1016.current.enabled) {
      return Math.max(
        1,
        windowFirstPageV114.current - 1 + Math.ceil(logs.length / pageSize) + (cursorV1016.current.next ? 1 : 0)
      );
    }
    return (
      windowFirstPageV114.current - 1 + Math.ceil(logs.length / pageSize) + (logs.length % pageSize === 0 ? 1 : 0)
    );
  };

  const handlePageSizeChangeV2 = (e, { value }) => {
    if (!pageSizeOptionsV105.some((option) => option.value === value)) return;
    setPageSize(value);
    setActivePage(1);
  };

  const sortLog = (key) => {
    if (logs.length === 0) return;
    setLoading(true);
    let sortedLogs = [...logs];
    if (key === 'quota') {
      sortedLogs.sort(compareCustomerMoneyV1014);
      if (sortedLogs[0] === logs[0]) sortedLogs.reverse();
      setLogs(sortedLogs);
      setLoading(false);
      return;
    }
    const getSortValue = (log) => {
      if (key === 'model_name') return getDisplayModelNameV4(log);
      return log[key];
    };
    if (typeof getSortValue(sortedLogs[0]) === 'string') {
      sortedLogs.sort((a, b) => {
        return ('' + getSortValue(a)).localeCompare(getSortValue(b));
      });
    } else {
      sortedLogs.sort((a, b) => {
        if (getSortValue(a) === getSortValue(b)) return 0;
        if (getSortValue(a) > getSortValue(b)) return -1;
        if (getSortValue(a) < getSortValue(b)) return 1;
        return 0;
      });
    }
    if (sortedLogs[0] === logs[0]) {
      sortedLogs.reverse();
    }
    setLogs(sortedLogs);
    setLoading(false);
  };

  const closeLogTextModal = () => {
    setSelectedLog(null);
  };

  const copyLogText = async (log) => {
    const detailText = log ? formatLogTextV822(log) : '';
    if (!detailText) {
      showWarning(t('log.messages.empty_detail'));
      return;
    }
    try {
      if (await copy(detailText)) {
        showSuccess(t('log.messages.copy_success'));
      } else {
        showWarning(t('log.messages.copy_failed'));
      }
    } catch {
      showWarning(t('log.messages.copy_failed'));
    }
  };

  return (
    <>
      <Header as='h3'>
        {t('log.usage_details')}（{t('log.total_quota')}：
        {showStat &&
          customerLogAmountV822(stat, (quota) => renderQuota(quota, t))}
        {!showStat && (
          <span
            onClick={handleEyeClick}
            style={{ cursor: 'pointer', color: 'gray' }}
          >
            {t('log.click_to_view')}
          </span>
        )}
        ）
      </Header>
      {showStat && stat.known_money && (
        <section data-testid='customer-money-summary-v1014'>
          <span>
            {t('customer_money_v1014.known_subtotal', {
              defaultValue: 'Known subtotal',
            })}
          </span>
          <dl className='log-detail-meta-v7'>
            {customerMoneyFieldsV1014
              .filter(([field]) => field !== 'state')
              .map(([field, zh, en]) => (
                <div key={field}>
                  <dt>
                    {t(`customer_money_v1014.${field}`, {
                      defaultValue: `${zh} / ${en}`,
                    })}
                  </dt>
                  <dd>
                    <code>
                      {customerMoneyV1014({ money: stat.known_money })?.[
                        field
                      ] ?? '-'}
                    </code>
                    {stat.unknown_money?.[field] > 0 && (
                      <span>
                        {' '}
                        ·{' '}
                        {t('customer_money_v1014.unknown_count', {
                          defaultValue: 'Unknown: {{count}}',
                          count: stat.unknown_money[field],
                        })}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
          </dl>
        </section>
      )}
      <div className='record-export-toolbar-v1023'>
        <Button
          as={Link}
          to={`/consumption-list?scope=${
            customerScope === 'special' ? 'special' : 'ordinary'
          }`}
          size='small'
          icon
          labelPosition='left'
        >
          <Icon name='file alternate outline' />
          {recordExportTextV1023(i18n?.language || 'zh', 'excel')}
        </Button>
      <RecordExportV1023 scope={customerScope} accountId={billing_account_id} admin={isAdminUser}
        filters={{ model_name, token_name, username, request_id, outcome, model_company, model_type,
          channel_query: isRootUser ? provider_family : '', provider_family: isRootUser ? '' : provider_family,
          routing_group: showDiagnostics ? routing_group : '', type: logType,
          error_only: errorOnly, user_input_error_only: userInputErrorOnly }} />
      </div>
      {isRootUser && <LogArchivePanelV119 language={i18n?.language || 'zh'} />}
      <Form
        className='log-query-shell-v101'
        onSubmit={(event) => {
          event.preventDefault();
          window.clearTimeout(enterQueryTimerV101.current);
          enterQueryTimerV101.current = null;
          refresh();
        }}
      >
        <div className='log-query-layout-v101'>
          <div className='log-query-calendar-v101'>
            <BillingDateCalendarV89
              billingDay={isAdminUser ? null : billingDayV89}
              value={selectedDatesV89}
              onChange={setSelectedDatesV89}
              onRefreshToday={refreshToday}
              compact
              eligibilityEndpoint={
                isAdminUser ? '' : '/api/billing/v8/self/eligibility-timeline'
              }
            />
            {dateIntervalsV89 !== appliedDateIntervalsV89 ? (
              <Label basic color='orange' className='log-calendar-pending-v89'>
                <Icon name='clock outline' />
                {t('billing.calendar.pending_query')}
              </Label>
            ) : null}
          </div>
          <div
            className='ui form log-filter-form-v72 log-query-form-v101'
            onKeyDownCapture={captureQueryEnterV101}
            onKeyDown={handleQueryEnterV101}
          >
            <Form.Group>
              <Form.Input
                fluid
                label={t('log.table.model_name')}
                size={'small'}
                width={3}
                value={model_name}
                placeholder={t('log.table.model_name_placeholder')}
                name='model_name'
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label={t('log.table.token_name')}
                size={'small'}
                width={3}
                value={token_name}
                placeholder={t('log.table.token_name_placeholder')}
                name='token_name'
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Input
                fluid
                label={customerText(
                  isRootUser ? 'channel_provider' : 'provider'
                )}
                placeholder={
                  isRootUser
                    ? customerText('channel_provider_placeholder')
                    : undefined
                }
                size='small'
                width={3}
                name='provider_family'
                value={provider_family}
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label={customerText('request_id')}
                size='small'
                width={6}
                name='request_id'
                value={request_id}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Dropdown
                fluid
                selection
                label={t('log.filters_v101.log_type')}
                aria-label={t('log.filters_v101.log_type')}
                name='logType'
                value={logType}
                options={LOG_OPTIONS}
                onChange={(event, { value }) => setLogType(value)}
              />
              {showDiagnostics && (
                <Form.Input
                  fluid
                  label={t('log.table.routing_group')}
                  size='small'
                  name='routing_group'
                  value={routing_group}
                  placeholder={t('log.filters_v101.routing_group_placeholder')}
                  maxLength={64}
                  onChange={handleInputChange}
                />
              )}
              <Form.Dropdown
                fluid
                selection
                label={t('log.filters_v101.outcome')}
                aria-label={t('log.filters_v101.outcome')}
                placeholder={t('log.filters_v101.all')}
                name='outcome'
                value={outcome}
                options={filterOptionsV101([
                  '',
                  'success',
                  'failure',
                  'canceled',
                  'unknown',
                ])}
                onChange={handleInputChange}
              />
              <Form.Dropdown
                fluid
                search
                selection
                label={t('log.filters_v101.model_company')}
                aria-label={t('log.filters_v101.model_company')}
                placeholder={t('log.filters_v101.all')}
                name='model_company'
                value={model_company}
                options={companyOptionsV101}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Dropdown
                fluid
                selection
                label={t('log.filters_v113.model_type')}
                aria-label={t('log.filters_v113.model_type')}
                name='model_type'
                value={model_type}
                options={MODEL_CATEGORY_FILTERS_V814.map((category) => ({
                  key: category,
                  value: category === 'all' ? '' : category,
                  text: category === 'all'
                    ? t('log.filters_v101.all')
                    : t(`models.catalog.category_${category}`),
                }))}
                onChange={handleInputChange}
              />
            </Form.Group>
            {!isAdminUser && organizationLogs && (
              <Form.Group>
                <Form.Input
                  className='log-query-wide-v101'
                  fluid
                  label={t('log.table.username')}
                  size='small'
                  width={4}
                  value={username}
                  placeholder={t('log.table.username_placeholder')}
                  name='username'
                  onChange={handleInputChange}
                />
              </Form.Group>
            )}
            {isAdminUser && (
              <>
                <Form.Group>
                  <Form.Dropdown
                    fluid
                    search
                    selection
                    clearable
                    label={t('log.table.organization')}
                    width={4}
                    name='billing_account_id'
                    value={billing_account_id}
                    options={organizationOptionsV815}
                    placeholder={t('log.organization_all_v815')}
                    onChange={handleInputChange}
                  />
                  <Form.Input
                    fluid
                    label={t('log.table.username')}
                    size={'small'}
                    width={3}
                    value={username}
                    placeholder={t('log.table.username_placeholder')}
                    name='username'
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </>
            )}
          </div>
        </div>
        <p role='note'>{customerText(includeArchivesV119 ? 'archive_help_v119' : 'recent_help_v119')}</p>
        <div className='log-query-toolbar-v101'>
          <Form.Group inline className='log-query-options-v101'>
            <Form.Checkbox
              label={customerText('include_archives_v119')}
              checked={includeArchivesV119}
              onChange={(event, { checked }) => {
                setActivePage(1);
                setIncludeArchivesV119(Boolean(checked));
              }}
            />
            <Form.Checkbox
              label={t('log.filters_v101.error_only', {
                defaultValue: '仅看错误 (Error Only)',
              })}
              checked={errorOnly}
              onChange={(e, { checked }) => {
                setActivePage(1);
                setErrorOnly(checked);
              }}
            />
            <Form.Checkbox
              label={t('log.filters.user_input_error_only_v820')}
              title={t('log.filters.request_rejection_help_v823')}
              checked={userInputErrorOnly}
              onChange={(e, { checked }) => {
                setActivePage(1);
                setUserInputErrorOnly(checked);
              }}
            />
            <Form.Checkbox
              label={t('log.filters_v101.text_mode', {
                defaultValue: '纯文本日志 (Text Mode)',
              })}
              checked={viewMode === 'text'}
              onChange={(e, { checked }) =>
                setViewMode(checked ? 'text' : 'table')
              }
            />
            <Form.Field className='log-query-page-size-v101'>
              <label>{t('log.table.page_size')}</label>
              <Select
                compact
                options={pageSizeOptionsV105}
                value={pageSize}
                onChange={handlePageSizeChangeV2}
              />
            </Form.Field>
            {viewMode === 'text' && (
              <Button
                type='button'
                size='mini'
                basic
                onClick={async () => {
                  const textOutput = getVisibleLogsV2()
                    .map((log) => formatLogTextV822(log))
                    .join(
                      '==================================================\n'
                    );
                  try {
                    if (await copy(textOutput))
                      showSuccess(t('log.messages.copy_success'));
                    else showWarning(t('log.messages.copy_failed'));
                  } catch {
                    showWarning(t('log.messages.copy_failed'));
                  }
                }}
              >
                {t('log.filters_v101.copy_all', {
                  defaultValue: '一键复制所有加载文本 (Copy All loaded)',
                })}
              </Button>
            )}
          </Form.Group>
          <Form.Field className='log-query-submit-v101'>
            <Button type='submit' primary size='small' loading={loading}>
              <Icon name='search' />
              {t('log.buttons.submit')}
            </Button>
          </Form.Field>
        </div>
      </Form>
      {viewMode === 'table' && (
        <Accordion fluid styled className='log-columns-v101'>
          <Accordion.Title
            active={columnSelectorOpen}
            onClick={() => setColumnSelectorOpen((open) => !open)}
          >
            <Icon name='dropdown' />
            {t('log.columns.title')} ({visibleColumnCountV7}/
            {availableColumnOptionsV7.length})
          </Accordion.Title>
          <Accordion.Content active={columnSelectorOpen}>
            <div className='log-column-selector-v72'>
              {availableColumnOptionsV7.map((column) => (
                <Checkbox
                  key={column.key}
                  label={column.label}
                  checked={isColumnVisibleV7(column.key)}
                  onChange={(event, { checked }) =>
                    toggleColumnV7(column.key, checked)
                  }
                />
              ))}
              <Button size='mini' basic onClick={resetColumnsV7}>
                {t('log.columns.reset')}
              </Button>
            </div>
          </Accordion.Content>
        </Accordion>
      )}
      {viewMode === 'table' ? (
        <>
          <div
            ref={tableScrollRefV113}
            className='log-table-scroll-v7'
            role='region'
            aria-label={customerText(customerScope)}
            tabIndex={0}
          >
            <Table
              basic='very'
              compact
              unstackable
              size='small'
              className='log-table-v7'
              aria-rowcount={currentPageLogsV114.length + 1}
              style={{ minWidth: `${Math.max(tableMinWidthV7, 720)}px` }}
            >
              <colgroup>
                {visibleColumnOptionsV7.map((column) => (
                  <col
                    key={column.key}
                    style={{ width: `${columnWidthsV815[column.key]}px` }}
                  />
                ))}
              </colgroup>
              <Table.Header>
                <Table.Row>
                  {visibleColumnOptionsV7.map((column) => {
                    const sortKeys = {
                      request_received_at: 'request_received_at',
                      time: 'created_at',
                      channel: 'channel_name',
                      client_app: 'client_app',
                      channel_key: 'channel_key_index',
                      type: 'type',
                      model: 'model_name',
                      username: 'username',
                      organization: 'organization_name',
                      token_name: 'token_name',
                      request_group: 'request_group',
                      routing_group: 'routing_group',
                      performance: 'elapsed_time',
                      prompt_tokens: 'prompt_tokens',
                      completion_tokens: 'completion_tokens',
                      quota: 'quota',
                      provider_family: 'provider_family',
                      customer_scope: 'customer_scope',
                      resolution: 'resolution',
                      actual_model: 'actual_model',
                    };
                    const sortKey = sortKeys[column.key];
                    return (
                      <ResizableHeaderCellV815
                        key={column.key}
                        columnKey={column.key}
                        width={columnWidthsV815[column.key]}
                        onResizeStart={startColumnResizeV815}
                        onResetWidth={resetColumnWidthV815}
                        onClick={sortKey ? () => sortLog(sortKey) : undefined}
                        style={{ cursor: sortKey ? 'pointer' : 'default' }}
                      >
                        {column.label}
                      </ResizableHeaderCellV815>
                    );
                  })}
                </Table.Row>
              </Table.Header>

              <tbody ref={tableBodyRefV115}>
                {paddingTopV114 > 0 && (
                  <tr aria-hidden='true' className='log-virtual-spacer-v114'>
                    <td colSpan={visibleColumnCountV7} style={{ height: paddingTopV114 }} />
                  </tr>
                )}
                {renderedRowsV114.map(({ index: idx, key }) => {
                  const log = currentPageLogsV114[idx];
                  return (
                    <tr
                      key={key}
                      data-index={idx}
                      aria-rowindex={idx + 2}
                      ref={virtualizedV114 ? rowVirtualizerV114.measureElement : undefined}
                      className={isErrorLogV72(log) ? 'log-error-row-v72' : ''}
                    >
                      {isColumnVisibleV7('request_received_at') && (
                        <Table.Cell>
                          {renderRequestReceivedTimestamp(
                            log.request_received_at
                          )}
                        </Table.Cell>
                      )}
                      {isColumnVisibleV7('time') && (
                        <Table.Cell>
                          {renderTimestamp(log.created_at, log.request_id)}
                        </Table.Cell>
                      )}
                      {showDiagnostics && isColumnVisibleV7('channel') && (
                        <Table.Cell>
                          {log.channel ? (
                            <Label
                              basic
                              as={isAdminUser ? Link : 'span'}
                              to={
                                isAdminUser
                                  ? `/channel/edit/${log.channel}`
                                  : undefined
                              }
                            >
                              {log.channel_name || `#${log.channel}`}
                            </Label>
                          ) : (
                            ''
                          )}
                        </Table.Cell>
                      )}
                      {isColumnVisibleV7('client_app') && (
                        <Table.Cell>{renderClientIdentityV73(log)}</Table.Cell>
                      )}
                      {showDiagnostics && isColumnVisibleV7('channel_key') && (
                        <Table.Cell>
                          {log.channel_key_index ? (
                            <Label basic>{`#${log.channel_key_index}`}</Label>
                          ) : log.channel_key_id ? (
                            <Label basic>{log.channel_key_id}</Label>
                          ) : (
                            ''
                          )}
                        </Table.Cell>
                      )}
                      {isColumnVisibleV7('type') && (
                        <Table.Cell>{renderType(log.type)}</Table.Cell>
                      )}
                      {isColumnVisibleV7('model') && (
                        <Table.Cell>
                          <div
                            className='log-cell-ellipsis-v7'
                            title={getDisplayModelNameV4(log)}
                          >
                            {getDisplayModelNameV4(log)
                              ? renderColorLabel(getDisplayModelNameV4(log))
                              : ''}
                          </div>
                        </Table.Cell>
                      )}
                      {showUserTokenQuota() && (
                        <>
                          {showUsername && isColumnVisibleV7('username') && (
                            <Table.Cell>
                              {log.user_id > 0 ? (
                                <Label
                                  basic
                                  as={isAdminUser ? Link : 'span'}
                                  to={
                                    isAdminUser
                                      ? `/user/edit/${log.user_id}`
                                      : undefined
                                  }
                                >
                                  {logPrincipalV823(log.username, log.user_id)}
                                </Label>
                              ) : (
                                logPrincipalV823(log.username, log.user_id)
                              )}
                            </Table.Cell>
                          )}
                        </>
                      )}
                      {showDiagnostics && isColumnVisibleV7('organization') && (
                        <Table.Cell>
                          <div
                            className='log-cell-ellipsis-v7'
                            title={logPrincipalV823(
                              log.organization_name,
                              log.billing_account_id
                            )}
                          >
                            {logPrincipalV823(
                              log.organization_name,
                              log.billing_account_id
                            )}
                          </div>
                        </Table.Cell>
                      )}
                      {showUserTokenQuota() &&
                        isColumnVisibleV7('token_name') && (
                          <Table.Cell>
                            <div
                              className='log-cell-ellipsis-v7'
                              title={logPrincipalV823(
                                log.token_name,
                                log.token_id
                              )}
                            >
                              {renderColorLabel(
                                logPrincipalV823(log.token_name, log.token_id)
                              )}
                            </div>
                          </Table.Cell>
                        )}
                      {isColumnVisibleV7('request_group') && (
                        <Table.Cell>
                          {log.request_group ? (
                            <Label basic color='teal'>
                              {log.request_group}
                            </Label>
                          ) : (
                            ''
                          )}
                        </Table.Cell>
                      )}
                      {isColumnVisibleV7('routing_group') && (
                        <Table.Cell>
                          {log.routing_group ? (
                            <Label basic color='blue'>
                              {log.routing_group}
                            </Label>
                          ) : (
                            ''
                          )}
                        </Table.Cell>
                      )}
                      {isColumnVisibleV7('performance') && (
                        <Table.Cell>{renderPerformanceV7(log, t)}</Table.Cell>
                      )}
                      {showUserTokenQuota() &&
                        isColumnVisibleV7('prompt_tokens') && (
                          <Table.Cell>
                            {customerLogUsageV822(log, 'prompt_tokens')}
                          </Table.Cell>
                        )}
                      {showUserTokenQuota() &&
                        isColumnVisibleV7('completion_tokens') && (
                          <Table.Cell>
                            {customerLogUsageV822(log, 'completion_tokens')}
                          </Table.Cell>
                        )}
                      {showUserTokenQuota() &&
                        isColumnVisibleV7('source_name') && (
                          <Table.Cell>
                            {commercialSourceV107(
                              log,
                              i18n?.language?.startsWith('zh')
                            )}
                          </Table.Cell>
                        )}
                      {showUserTokenQuota() &&
                        isColumnVisibleV7('effective_multiplier') && (
                          <Table.Cell>
                            {commercialMultiplierV107(
                              log,
                              i18n?.language?.startsWith('zh')
                            )}
                            {log.billing_mode === 'openlux_receipt' && (
                              <div className='log-commercial-note-v107'>
                                OpenLux ×1.5
                              </div>
                            )}
                          </Table.Cell>
                        )}
                      {showUserTokenQuota() && isColumnVisibleV7('quota') && (
                        <Table.Cell>
                          {customerLogAmountV822(log, (quota) =>
                            renderQuota(quota, t, 6)
                          )}
                          {[
                            'openlux_receipt',
                            'openlux_deferred',
                            'base_source_receipt',
                          ].includes(log.billing_mode) && (
                            <div className='log-commercial-note-v107'>
                              {commercialReceiptStateV107(
                                log,
                                i18n?.language?.startsWith('zh')
                              )}
                              {log.upstream_error && (
                                <div>{log.upstream_error}</div>
                              )}
                            </div>
                          )}
                        </Table.Cell>
                      )}
                      {isColumnVisibleV7('detail') && (
                        <Table.Cell className='log-detail-column-v7'>
                          <RetrySummaryV1021
                            value={log.retry_summary_v1021}
                            requestID={log.request_id}
                          />
                          {renderDetail(log, setSelectedLog, t)}
                        </Table.Cell>
                      )}
                      {showDiagnostics &&
                        CUSTOMER_LOG_COLUMNS_V822.map((field) =>
                          isColumnVisibleV7(field) ? (
                            <Table.Cell key={field}>
                              <div className='log-cell-ellipsis-v7'>
                                {field === 'customer_scope'
                                  ? customerText(
                                      ['ordinary', 'special'].includes(
                                        log.customer_scope
                                      )
                                        ? log.customer_scope
                                        : 'unknown'
                                    )
                                  : field === 'provider_family' && isRootUser
                                  ? logProviderNameV106(log)
                                  : customerLogValueV822(log[field])}
                              </div>
                            </Table.Cell>
                          ) : null
                        )}
                    </tr>
                  );
                })}
                {paddingBottomV114 > 0 && (
                  <tr aria-hidden='true' className='log-virtual-spacer-v114'>
                    <td colSpan={visibleColumnCountV7} style={{ height: paddingBottomV114 }} />
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className='log-table-pagination-v113'>
            <Button size='small' onClick={refresh} loading={loading}>
              {t('log.buttons.refresh')}
            </Button>
            <div className='log-table-page-size-v113'>
              <span>{t('log.table.page_size')}</span>
              <Select
                compact
                options={pageSizeOptionsV105}
                value={pageSize}
                onChange={handlePageSizeChangeV2}
              />
            </div>
            <Pagination
              activePage={activePage}
              onPageChange={onPaginationChange}
              size='small'
              siblingRange={1}
              totalPages={getTotalPagesV2()}
            />
          </div>
        </>
      ) : (
        <Segment basic>
          <pre className='log-text-view-v7'>
            {getCurrentPageLogsV2()
              .map((log) => formatLogTextV822(log))
              .join('==================================================\n')}
          </pre>
          <div className='log-text-toolbar-v72'>
            <div className='log-text-toolbar-controls-v72'>
              <Button size='small' onClick={refresh} loading={loading}>
                {t('log.buttons.refresh')}
              </Button>
              <span style={{ marginLeft: '12px', marginRight: '8px' }}>
                {t('log.table.page_size')}
              </span>
              <Select
                compact
                options={pageSizeOptionsV105}
                value={pageSize}
                onChange={handlePageSizeChangeV2}
              />
            </div>
            <Pagination
              activePage={activePage}
              onPageChange={onPaginationChange}
              size='small'
              siblingRange={1}
              totalPages={getTotalPagesV2()}
            />
          </div>
        </Segment>
      )}
      <Modal
        className='log-detail-modal-v72'
        open={!!selectedLog}
        onClose={closeLogTextModal}
        size='large'
      >
        <Modal.Header>{t('log.modal.title')}</Modal.Header>
        <Modal.Content scrolling>
          {selectedLog && (
            <div className='log-detail-meta-v7'>
              <div>
                <span>{t('log.table.request_received_at')}</span>
                <code>
                  {selectedLog.request_received_at
                    ? timestamp2string(selectedLog.request_received_at)
                    : '-'}
                </code>
              </div>
              <div>
                <span>{t('log.table.time')}</span>
                <code>
                  {selectedLog.created_at
                    ? timestamp2string(selectedLog.created_at)
                    : '-'}
                </code>
              </div>
              <div>
                <span>Request ID</span>
                <code>{selectedLog.request_id || '-'}</code>
              </div>
              <div>
                <span>{t('log.table.model')}</span>
                <strong>{getDisplayModelNameV4(selectedLog) || '-'}</strong>
              </div>
              {showDiagnostics && (
                <div>
                  <span>{t('log.table.channel')}</span>
                  <strong>
                    {selectedLog.channel_name ||
                      (selectedLog.channel ? `#${selectedLog.channel}` : '-')}
                  </strong>
                </div>
              )}
              {showDiagnostics && (
                <div>
                  <span>{t('log.modal.project_reference')}</span>
                  <strong>{selectedLog.project_reference || '-'}</strong>
                </div>
              )}
              {showDiagnostics && (
                <div>
                  <span>{t('log.modal.service_tier')}</span>
                  <strong>{selectedLog.service_tier || '-'}</strong>
                </div>
              )}
              {showDiagnostics && (
                <>
                  <div>
                    <span>{t('log.modal.client_app')}</span>
                    <strong>
                      {logClientIdentityV94(selectedLog).name || '-'}
                    </strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_version')}</span>
                    <strong>
                      {logClientIdentityV94(selectedLog).version || '-'}
                    </strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_sdk')}</span>
                    <strong>{selectedLog.client_sdk || '-'}</strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_sdk_version')}</span>
                    <strong>{selectedLog.client_sdk_version || '-'}</strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_runtime')}</span>
                    <strong>{selectedLog.client_runtime || '-'}</strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_origin')}</span>
                    <strong>{selectedLog.client_origin || '-'}</strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_detection')}</span>
                    <strong>
                      {selectedLog.client_detection_source || '-'} /{' '}
                      {selectedLog.client_detection_confidence || '-'}
                    </strong>
                  </div>
                  <div className='log-detail-wide-v73'>
                    <span>User-Agent</span>
                    <strong>{selectedLog.client_user_agent || '-'}</strong>
                  </div>
                  <div>
                    <span>{t('log.modal.client_request_format')}</span>
                    <strong>{selectedLog.client_request_format || '-'}</strong>
                  </div>
                </>
              )}
              {showDiagnostics && (
                <div>
                  <span>{t('log.modal.provider_request_format')}</span>
                  <strong>{selectedLog.provider_request_format || '-'}</strong>
                </div>
              )}
              {showDiagnostics && (
                <div>
                  <span>{t('log.modal.protocol_mode')}</span>
                  <strong>{selectedLog.protocol_mode || '-'}</strong>
                </div>
              )}
              <div>
                <span>{t('log.modal.error_code')}</span>
                <strong>{selectedLog.error_code || '-'}</strong>
              </div>
              <div>
                <span>HTTP Status</span>
                <strong>{selectedLog.http_status || '-'}</strong>
              </div>
              {showDiagnostics && (
                <div>
                  <span>{t('log.table.organization')}</span>
                  <strong>
                    {selectedLog.organization_name ||
                      (selectedLog.billing_account_id
                        ? `#${selectedLog.billing_account_id}`
                        : '-')}
                  </strong>
                </div>
              )}
              <div>
                <span>{t('log.table.username')}</span>
                <strong>{selectedLog.username || '-'}</strong>
              </div>
              {showDiagnostics && (
                <div>
                  <span>{t('log.table.request_group')}</span>
                  <strong>{selectedLog.request_group || '-'}</strong>
                </div>
              )}
              {showDiagnostics && (
                <div>
                  <span>{t('log.table.routing_group')}</span>
                  <strong>{selectedLog.routing_group || '-'}</strong>
                </div>
              )}
            </div>
          )}
          <pre className='log-detail-content-v7'>
            {selectedLog?.content || ''}
          </pre>
          {selectedLog && (
            <CustomerLogRequestDetailV822
              onRetryAudit={applyRetryAuditV1022}
              key={`${customerScope}:${
                selectedLog.request_id || selectedLog.id
              }`}
              log={selectedLog}
              onDisclosure={applyDetailDisclosureV105}
              customerScope={customerScope}
              admin={isAdminUser}
              root={isRootUser}
              t={t}
            />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => copyLogText(selectedLog)}>
            {t('log.buttons.copy_text')}
          </Button>
          <Button onClick={closeLogTextModal}>{t('log.buttons.close')}</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

const LogsTableScopeV822 = ({ customerScope = 'ordinary' }) => {
  const { t, i18n } = useTranslation();
  const [resetVersion, setResetVersion] = useState(0);
  const isAdminUser = isAdmin();
  const isRootUser = isRoot();
  const baseScope = customerScope === 'special' ? 'special' : 'ordinary';
  const [classification, setClassification] = useSessionViewStateV81(
    `log-view.v8.24.${baseScope}.${
      isRootUser ? 'root' : 'self'
    }.classification`,
    { unknown: false }
  );
  const scope = isRootUser && classification.unknown ? 'unknown' : baseScope;
  if (baseScope === 'special' && isAdminUser && !isRootUser) {
    return (
      <Message warning content={customerLogTextV822(t, 'root_required')} />
    );
  }
  return (
    <>
      {isRootUser && (
        <Form className='customer-log-scope-v822'>
          <Form.Dropdown
            selection
            label={customerLogTextV822(t, 'classification')}
            value={scope}
            options={[baseScope, 'unknown'].map((value) => ({
              key: value,
              value,
              text: customerLogTextV822(t, value),
            }))}
            onChange={(event, { value }) =>
              setClassification({
                ...classification,
                unknown: value === 'unknown',
              })
            }
          />
          <Form.Checkbox
            label='空转与计费异常'
            checked={classification.audit === true}
            onChange={(event, { checked }) =>
              setClassification({ ...classification, audit: checked })
            }
          />
        </Form>
      )}
      <div className='record-export-toolbar-v1023'>
        <Button type='button' size='small' className='record-export-reset-v1023'
          onClick={() => {
            setClassification({ unknown: false, audit: false });
            setResetVersion((value) => value + 1);
          }}>
          <Icon name='undo' />{recordExportTextV1023(i18n?.language, 'reset')}
        </Button>
      </div>
      {isRootUser && classification.audit === true ? (
        <RequestAuditV97 key={scope} root customerScope={scope} />
      ) : (
        <LogsTableViewV822
          key={`${scope}.${
            isRootUser ? 'root' : isAdminUser ? 'admin' : 'self'
          }`}
          customerScope={scope}
          isAdminUser={isAdminUser}
          isRootUser={isRootUser}
          resetVersion={resetVersion}
        />
      )}
    </>
  );
};

const LogsTable = ({ customerScope = 'ordinary' }) => (
  <LogsTableScopeV822
    key={`${customerScope === 'special' ? 'special' : 'ordinary'}.${
      isRoot() ? 'root' : isAdmin() ? 'admin' : 'self'
    }`}
    customerScope={customerScope}
  />
);

export default LogsTable;
